import { useEffect, useState } from "react";
import { NavLink, Link } from "react-router-dom";

import {
  FaChevronDown,
  FaBars,
  FaTimes,
  FaLock,
  FaPhoneAlt,
  FaWhatsapp,
} from "react-icons/fa";

import servicesData from "../../data/servicesData";
import useWebsiteSettings from "../../hooks/useWebsiteSettings";

import "./Navbar.css";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);

  const { settings } = useWebsiteSettings();

  // =====================================================
  // SCROLL EFFECT
  // =====================================================

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // =====================================================
  // PREVENT BODY SCROLL WHEN MOBILE MENU IS OPEN
  // =====================================================

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  // =====================================================
  // CLOSE MENU
  // =====================================================

  const closeMenu = () => {
    setMenuOpen(false);
    setServicesOpen(false);
  };

  // =====================================================
  // SERVICES TOGGLE
  // =====================================================

  const toggleServices = () => {
    setServicesOpen((previous) => !previous);
  };

  // =====================================================
  // PHONE
  // =====================================================

  const phoneNumber = settings.phone || "+91 9999999999";

  const phoneHref = `tel:${phoneNumber.replace(/\D/g, "")}`;

  // =====================================================
  // WHATSAPP
  // =====================================================

  const whatsappNumber = settings.whatsapp || settings.phone || "9999999999";

  const whatsappHref = `https://wa.me/${whatsappNumber.replace(/\D/g, "")}`;

  return (
    <>
      {/* =================================================
          NAVBAR
      ================================================= */}

      <header className={`navbar ${scrolled ? "scrolled" : ""}`}>
        <div className="nav-container">
          {/* =================================================
              LOGO
          ================================================= */}

          <Link
            to="/"
            className="logo"
            onClick={closeMenu}
            aria-label="Quality Sports Surface Home"
          >
            <img
              src="/images/logo.png"
              alt="Quality Sports Surface"
              className="logo-image"
            />
          </Link>

          {/* =================================================
              DESKTOP NAVIGATION
          ================================================= */}

          <nav className="desktop-nav">
            <NavLink
              to="/"
              className={({ isActive }) =>
                isActive ? "nav-item active" : "nav-item"
              }
            >
              Home
            </NavLink>

            <NavLink
              to="/about"
              className={({ isActive }) =>
                isActive ? "nav-item active" : "nav-item"
              }
            >
              About
            </NavLink>

            {/* SERVICES */}

            <div
              className={`services-dropdown ${servicesOpen ? "open" : ""}`}
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <button
                type="button"
                className="services-link"
                onClick={toggleServices}
                aria-expanded={servicesOpen}
              >
                <span>Our Services</span>

                <FaChevronDown
                  className={`down-icon ${servicesOpen ? "rotate" : ""}`}
                />
              </button>

              {servicesOpen && (
                <div className="dropdown-menu">
                  {servicesData.map((service) => (
                    <NavLink
                      key={service.id}
                      to={service.route}
                      className={({ isActive }) =>
                        isActive ? "dropdown-item active" : "dropdown-item"
                      }
                      onClick={closeMenu}
                    >
                      {service.title}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>

            <NavLink
              to="/projects"
              className={({ isActive }) =>
                isActive ? "nav-item active" : "nav-item"
              }
            >
              Projects
            </NavLink>

            <NavLink
              to="/gallery"
              className={({ isActive }) =>
                isActive ? "nav-item active" : "nav-item"
              }
            >
              Gallery
            </NavLink>
            <NavLink
              to="/blogs"
              className={({ isActive }) =>
                isActive ? "nav-item active" : "nav-item"
              }
            >
              Blogs
            </NavLink>
            <NavLink
              to="/contact"
              className={({ isActive }) =>
                isActive ? "nav-item active" : "nav-item"
              }
            >
              Contact
            </NavLink>
          </nav>

          {/* =================================================
              DESKTOP RIGHT ACTIONS
          ================================================= */}

          <div className="navbar-actions">
            {/* PHONE */}

            <a
              href={phoneHref}
              className="contact-action phone-action"
              aria-label={`Call ${phoneNumber}`}
            >
              <FaPhoneAlt />

              <span>{phoneNumber}</span>
            </a>

            {/* WHATSAPP */}

            <a
              href={whatsappHref}
              className="contact-action whatsapp-action"
              target="_blank"
              rel="noreferrer"
              aria-label="Contact us on WhatsApp"
            >
              <FaWhatsapp />

              {/* <span>WhatsApp</span> */}
            </a>

            {/* ADMIN */}

            <Link to="/admin/login" className="admin-login-btn">
              <FaLock />

              <span>Admin Login</span>
            </Link>

            {/* GET QUOTE */}

            {/* <Link to="/contact" className="quote-btn desktop-quote">
              Get Quote
            </Link> */}
          </div>

          {/* =================================================
              MOBILE MENU BUTTON
          ================================================= */}

          <button
            type="button"
            className="menu-btn"
            onClick={() => setMenuOpen((previous) => !previous)}
            aria-label={
              menuOpen ? "Close navigation menu" : "Open navigation menu"
            }
            aria-expanded={menuOpen}
          >
            {menuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </header>

      {/* =================================================
          MOBILE MENU
      ================================================= */}

      <div className={`mobile-menu ${menuOpen ? "active" : ""}`}>
        {/* MOBILE MENU HEADER */}

        <div className="mobile-menu-header">
          <img
            src="/images/logo.png"
            alt="Quality Sports Surface"
            className="mobile-menu-logo"
          />

          <button
            type="button"
            className="mobile-close-btn"
            onClick={closeMenu}
            aria-label="Close menu"
          >
            <FaTimes />
          </button>
        </div>

        {/* MOBILE NAVIGATION */}

        <nav className="mobile-nav">
          <NavLink
            to="/"
            onClick={closeMenu}
            className={({ isActive }) =>
              isActive ? "mobile-nav-item active" : "mobile-nav-item"
            }
          >
            Home
          </NavLink>

          <NavLink
            to="/about"
            onClick={closeMenu}
            className={({ isActive }) =>
              isActive ? "mobile-nav-item active" : "mobile-nav-item"
            }
          >
            About
          </NavLink>

          {/* MOBILE SERVICES */}

          <div className="mobile-services">
            <button
              type="button"
              className="mobile-services-btn"
              onClick={toggleServices}
              aria-expanded={servicesOpen}
            >
              <span>Services</span>

              <FaChevronDown
                className={
                  servicesOpen ? "mobile-chevron rotate" : "mobile-chevron"
                }
              />
            </button>

            {servicesOpen && (
              <div className="mobile-services-list">
                {servicesData.map((service) => (
                  <NavLink
                    key={service.id}
                    to={service.route}
                    onClick={closeMenu}
                    className="mobile-service-item"
                  >
                    {service.title}
                  </NavLink>
                ))}
              </div>
            )}
          </div>

          <NavLink
            to="/projects"
            onClick={closeMenu}
            className={({ isActive }) =>
              isActive ? "mobile-nav-item active" : "mobile-nav-item"
            }
          >
            Projects
          </NavLink>

          <NavLink
            to="/gallery"
            onClick={closeMenu}
            className={({ isActive }) =>
              isActive ? "mobile-nav-item active" : "mobile-nav-item"
            }
          >
            Gallery
          </NavLink>
          <NavLink
            to="/blogs"
            onClick={closeMenu}
            className={({ isActive }) =>
              isActive ? "mobile-nav-item active" : "mobile-nav-item"
            }
          >
            Blogs
          </NavLink>
          <NavLink
            to="/contact"
            onClick={closeMenu}
            className={({ isActive }) =>
              isActive ? "mobile-nav-item active" : "mobile-nav-item"
            }
          >
            Contact
          </NavLink>
        </nav>

        {/* MOBILE CONTACT */}

        <div className="mobile-contact">
          <a href={phoneHref} className="mobile-contact-btn phone">
            <FaPhoneAlt />

            <span>Call {phoneNumber}</span>
          </a>

          <a
            href={whatsappHref}
            target="_blank"
            rel="noreferrer"
            className="mobile-contact-btn whatsapp"
          >
            <FaWhatsapp />

            <span>WhatsApp</span>
          </a>
        </div>

        {/* MOBILE ADMIN */}

        <Link
          to="/admin/login"
          className="mobile-admin-btn"
          onClick={closeMenu}
        >
          <FaLock />

          <span>Admin Login</span>
        </Link>

        {/* MOBILE QUOTE */}

        {/* <Link to="/contact" className="mobile-quote-btn" onClick={closeMenu}>
          Get Free Quote
        </Link> */}
      </div>

      {/* =================================================
          OVERLAY
      ================================================= */}

      {menuOpen && (
        <div className="menu-overlay" onClick={closeMenu} aria-hidden="true" />
      )}
    </>
  );
};

export default Navbar;
